/* eslint-disable react-perf/jsx-no-new-function-as-prop */
'use client';
import {useEffect, useState} from 'react';
import {RiCheckLine, RiCloseFill} from 'react-icons/ri';
import {Popover, PopoverClose, PopoverContent, PopoverTrigger} from '@radix-ui/react-popover';

import {getPostalLocation} from '@/modules/foundation/shared/utils';
import {roundToTwoDecimals} from '@/shared/utils';

interface Props {
	setFreightCost: (cost: number) => void;
	setPostalCode: (code: string) => void;
	postalCode: string;
	freightCost: number;
}

const FreightCostDisplay: React.FC<Props> = ({setFreightCost, setPostalCode, freightCost = 0}) => {
	const [value, setValue] = useState('');
	const [postalCity, setPostalCity] = useState('');
	const [localPostalCode, setLocalPostalCode] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Load from sessionStorage on mount
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const savedPostalCode = sessionStorage.getItem('postalCode');
		if (savedPostalCode && savedPostalCode.length === 4) {
			setValue(savedPostalCode);
			fetchFreightCost(savedPostalCode);
		}
	}, []);

	// Lookup postal city when typing
	useEffect(() => {
		if (value.length === 4) {
			getPostalLocation(value).then(setPostalCity);
		} else {
			setPostalCity('');
		}
	}, [value]);

	// Fetch freight cost (used both on mount and on submit)
	const fetchFreightCost = async (code?: string) => {
		const postalCodeToUse = code || value;

		if (postalCodeToUse.length !== 4) {
			setError('Postnummer må fylles ut');
			return;
		}
		setLoading(true);

		setError(null);
		setPostalCode(postalCodeToUse);
		setLocalPostalCode(postalCodeToUse);
		sessionStorage.setItem('postalCode', postalCodeToUse);
		setPostalCity(await getPostalLocation(postalCodeToUse));

		try {
			setFreightCost(0);

			const res = await fetch('/api/shipping', {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({postalCode: postalCodeToUse}),
			});
			const data = await res.json();

			if ((!res.ok || data.error) && data.freightCost === null) {
				throw new Error(data.error || 'Kunne ikke hente fraktpris');
			}

			setFreightCost(data.freightCost);
			window.dataLayer?.push({
				event: 'shipping_quote',
				// @ts-expect-error custom type
				shipping_address: {postal_code: postalCodeToUse, country: 'NO'},
				shipping_cost: roundToTwoDecimals(data.freightCost / 100),
			});
		} catch {
			setFreightCost(0);
			setPostalCity('');
			setPostalCode('');
			setError('Kunne ikke hente fraktpris');
			window.dataLayer?.push({
				event: 'shipping_quote',
				// @ts-expect-error custom type
				shipping_address: {postal_code: postalCodeToUse, country: 'NO'},
				shipping_cost: 0,
				shipping_available: false,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<div>
			<div
				className={`flex gap-1 ${freightCost > 0 ? 'w-full justify-between' : 'justify-start'} ${localPostalCode && freightCost === 0 ? 'flex-col' : ''}`}
			>
				<span>Frakt{localPostalCode && ' til ' + localPostalCode}: </span>
				{freightCost > 0 ? (
					<span>
						<strong>{freightCost / 100} kr</strong>
					</span>
				) : (
					localPostalCode &&
					loading === false && (
						<span>
							<strong>Vi tilbyr ikke frakt til dette postnummeret.</strong>
						</span>
					)
				)}
				{freightCost === 0 && !localPostalCode && (
					<Popover>
						<PopoverTrigger asChild>
							<button className="text-blue-600 underline">
								{value.length === 0 ? 'Oppgi postnummer' : 'Endre postnummer'}
							</button>
						</PopoverTrigger>
						<PopoverContent
							className="relative max-w-[384px] border border-border-soft bg-white p-5"
							align="center"
							side="bottom"
						>
							<div className="absolute -top-2 left-1/2 size-0 -translate-x-1/2 border-x-8 border-b-8 border-transparent border-b-border-soft">
								<div className="absolute left-[-7px] top-[2px] size-0 border-x-[7px] border-b-[7px] border-transparent border-b-white" />
							</div>

							<h3 className="font-bold">Skriv ditt postnummer</h3>
							<div className="mt-2 flex items-center gap-2">
								<input
									type="text"
									name="postnummer"
									id="postnummer"
									value={value}
									onChange={(e) => setValue(e.target.value)}
									className="w-full max-w-[90px] rounded-[5px] border-2 border-border-medium p-2 focus:border-border-positive focus:outline-none focus:ring-0"
								/>
								<span className="text-gray-500">
									{!postalCity ? 'Poststed hentes automatisk' : postalCity}
								</span>
							</div>

							<div className="mt-4 flex gap-2">
								<PopoverClose asChild>
									<button
										className="flex items-center justify-center rounded-md bg-primary px-3 py-2 font-bold text-white disabled:bg-disabled"
										disabled={value.length < 4 || loading}
										onClick={() => fetchFreightCost()}
									>
										{loading ? (
											'Laster...'
										) : (
											<>
												<RiCheckLine className="mr-2" /> Bruk
											</>
										)}
									</button>
								</PopoverClose>
								<PopoverClose asChild>
									<button className="flex items-center justify-center rounded-md border-2 border-[#615E5C] px-3 py-2 font-bold">
										<RiCloseFill className="mr-2" size={16} /> Avbryt
									</button>
								</PopoverClose>
							</div>
						</PopoverContent>
					</Popover>
				)}
			</div>

			{localPostalCode && (
				<Popover>
					<PopoverTrigger asChild>
						<button className="text-blue-600 underline">
							{value.length === 0 ? 'Oppgi postnummer' : 'Endre postnummer'}
						</button>
					</PopoverTrigger>
					<PopoverContent
						className="relative max-w-[384px] border border-border-soft bg-white p-5"
						align="center"
						side="bottom"
					>
						<div className="absolute -top-2 left-1/2 size-0 -translate-x-1/2 border-x-8 border-b-8 border-transparent border-b-border-soft">
							<div className="absolute left-[-7px] top-[2px] size-0 border-x-[7px] border-b-[7px] border-transparent border-b-white" />
						</div>

						<h3 className="font-bold">Skriv ditt postnummer</h3>
						<div className="mt-2 flex items-center gap-2">
							<input
								type="text"
								name="postnummer"
								id="postnummer"
								value={value}
								onChange={(e) => setValue(e.target.value)}
								className="w-full max-w-[90px] rounded-[5px] border-2 border-border-medium p-2 focus:border-border-positive focus:outline-none focus:ring-0"
							/>
							<span className="text-gray-500">
								{!postalCity ? 'Poststed hentes automatisk' : postalCity}
							</span>
						</div>

						<div className="mt-4 flex gap-2">
							<PopoverClose asChild>
								<button
									className="flex items-center justify-center rounded-md bg-primary px-3 py-2 font-bold text-white disabled:bg-disabled"
									disabled={value.length < 4 || loading}
									onClick={() => fetchFreightCost()}
								>
									{loading ? (
										'Laster...'
									) : (
										<>
											<RiCheckLine className="mr-2" /> Bruk
										</>
									)}
								</button>
							</PopoverClose>
							<PopoverClose asChild>
								<button className="flex items-center justify-center rounded-md border-2 border-[#615E5C] px-3 py-2 font-bold">
									<RiCloseFill className="mr-2" size={16} /> Avbryt
								</button>
							</PopoverClose>
						</div>
					</PopoverContent>
				</Popover>
			)}

			{error && <p className="mt-2 text-sm text-[#BE0B1C]">{error}</p>}
		</div>
	);
};

export default FreightCostDisplay;
